import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TaskDetailDrawer } from '@/components/tasks/TaskDetailDrawer';

interface Task {
  id: string;
  title: string;
  body: string | null;
  dueDate: string | null;
  status: string;
  priority: string;
  source: string;
  sourceRef: string | null;
  opportunityId: string | null;
  assigneeId: string | null;
  completedAt: string | null;
}

type StatusFilter = 'OPEN' | 'DONE' | 'ALL';

const PRIORITY_BADGE: Record<string, string> = {
  URGENT: 'bg-red-900/40 text-red-200',
  HIGH: 'bg-amber-900/40 text-amber-200',
  NORMAL: 'bg-muted text-foreground',
  LOW: 'bg-card text-muted-foreground',
};

const SOURCE_BADGE: Record<string, string> = {
  MANUAL: 'bg-blue-950/40 text-blue-300',
  STALE_ALERT: 'bg-amber-950/40 text-amber-300',
  STAGE_GATE: 'bg-purple-950/40 text-purple-300',
  AGENT: 'bg-emerald-950/40 text-emerald-300',
  MENTION: 'bg-cyan-950/40 text-cyan-300',
  APPROVAL_REQUEST: 'bg-pink-950/40 text-pink-300',
};

const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

function isOverdue(dueDate: string | null, status: string): boolean {
  if (!dueDate || status === 'DONE' || status === 'CANCELLED') return false;
  return new Date(dueDate) < new Date(new Date().toDateString());
}

export function TasksInboxPage() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [detailTask, setDetailTask] = useState<Task | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params =
        statusFilter === 'ALL' ? {} : { status: statusFilter };
      const { data } = await apiClient.get<Task[]>('/tasks', { params });
      setTasks(data);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  function showToastMsg(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function complete(id: string) {
    try {
      await apiClient.post(`/tasks/${id}/complete`, {});
      showToastMsg('Task completed');
      load();
    } catch {
      showToastMsg('Failed to complete');
    }
  }

  async function setInProgress(id: string) {
    try {
      await apiClient.patch(`/tasks/${id}`, { status: 'IN_PROGRESS' });
      load();
    } catch {
      showToastMsg('Failed to update');
    }
  }

  const filters: StatusFilter[] = ['OPEN', 'DONE', 'ALL'];

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      <CreateTaskModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          showToastMsg('Task created');
          load();
        }}
      />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('nav.tasks')}</h1>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={viewMode === 'list' ? 'default' : 'outline'}
            onClick={() => setViewMode('list')}
          >
            List
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'board' ? 'default' : 'outline'}
            onClick={() => { setViewMode('board'); setStatusFilter('ALL'); }}
          >
            Board
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            + Task
          </Button>
          {filters.map((f) => (
            <Button
              key={f}
              variant={statusFilter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(f)}
            >
              {f}
            </Button>
          ))}
        </div>
      </div>

      {loading && tasks.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}

      {viewMode === 'board' && (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {(['OPEN', 'IN_PROGRESS', 'DONE'] as const).map((lane) => {
            const laneTasks = tasks.filter((task) => task.status === lane);
            return (
              <div
                key={lane}
                className="flex w-72 min-w-[18rem] flex-shrink-0 flex-col rounded-lg border bg-muted/50"
              >
                <div className="flex items-center justify-between border-b px-3 py-2">
                  <span className="text-sm font-medium">{lane}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {laneTasks.length}
                  </span>
                </div>
                <div className="flex-1 space-y-2 p-2" style={{ minHeight: 80 }}>
                  {laneTasks.map((task) => {
                    const overdue = isOverdue(task.dueDate, task.status);
                    return (
                      <div
                        key={task.id}
                        className="rounded-md border bg-card p-3 shadow-sm"
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <button
                            onClick={() =>
                              task.status !== 'DONE' ? complete(task.id) : undefined
                            }
                            disabled={task.status === 'DONE'}
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              task.status === 'DONE'
                                ? 'border-emerald-500 bg-emerald-500 text-white'
                                : 'border-border'
                            }`}
                          >
                            {task.status === 'DONE' && (
                              <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDetailTask(task)}
                            className="text-left text-sm font-medium leading-tight hover:underline"
                            data-testid="task-open-detail"
                          >
                            {task.title}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className={`rounded-full px-1.5 py-0.5 font-medium ${PRIORITY_BADGE[task.priority] ?? 'bg-muted'}`}>
                            {task.priority}
                          </span>
                          {overdue && (
                            <span className="font-medium text-red-600">Overdue</span>
                          )}
                          {task.dueDate && !overdue && (
                            <span className="text-muted-foreground">
                              {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                        {task.status === 'OPEN' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 h-7 text-xs"
                            onClick={() => setInProgress(task.id)}
                          >
                            Start
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode === 'list' && <ul className="divide-y rounded-lg border bg-card">
        {tasks.map((task) => {
          const overdue = isOverdue(task.dueDate, task.status);
          return (
            <li
              key={task.id}
              className={`flex items-start gap-3 px-4 py-3 ${
                task.status === 'DONE' ? 'opacity-60' : ''
              }`}
            >
              <button
                onClick={() =>
                  task.status === 'DONE' ? undefined : complete(task.id)
                }
                disabled={task.status === 'DONE' || task.status === 'CANCELLED'}
                className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                  task.status === 'DONE'
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-border hover:border-primary'
                }`}
                title={task.status === 'DONE' ? 'Completed' : 'Mark done'}
              >
                {task.status === 'DONE' && (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`font-medium ${
                      task.status === 'DONE' ? 'line-through' : ''
                    }`}
                  >
                    {task.title}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      PRIORITY_BADGE[task.priority] ?? 'bg-muted'
                    }`}
                  >
                    {task.priority}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      SOURCE_BADGE[task.source] ?? 'bg-card text-muted-foreground'
                    }`}
                  >
                    {task.source}
                  </span>
                  {task.status === 'IN_PROGRESS' && (
                    <span className="rounded-full bg-blue-900/40 px-2 py-0.5 text-[10px] font-medium text-blue-200">
                      IN PROGRESS
                    </span>
                  )}
                </div>
                {task.body && (
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                    {task.body}
                  </p>
                )}
                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  {task.dueDate && (
                    <span className={overdue ? 'font-medium text-red-600' : ''}>
                      {overdue ? 'Overdue: ' : 'Due: '}
                      {new Date(task.dueDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                  {task.opportunityId && (
                    <Link
                      to={`/opportunities/${task.opportunityId}`}
                      className="hover:underline"
                    >
                      View opportunity
                    </Link>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {task.status === 'OPEN' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setInProgress(task.id)}
                  >
                    Start
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDetailTask(task)}
                  data-testid="task-open-detail"
                >
                  {t('tasksExt.details')}
                </Button>
              </div>
            </li>
          );
        })}
        {!loading && tasks.length === 0 && (
          <li className="px-4 py-8 text-center text-sm text-muted-foreground">
            No tasks
          </li>
        )}
      </ul>}

      {detailTask && (
        <TaskDetailDrawer
          taskId={detailTask.id}
          taskTitle={detailTask.title}
          onClose={() => setDetailTask(null)}
        />
      )}
    </div>
  );
}

function CreateTaskModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/tasks', {
        title: title.trim(),
        body: body.trim() || undefined,
        dueDate: dueDate || undefined,
        priority,
        source: 'MANUAL',
      });
      setTitle('');
      setBody('');
      setDueDate('');
      setPriority('NORMAL');
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">Create Task</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Details..."
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Due Date</label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting || !title.trim()}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
